import { useEffect, useState, useMemo } from 'react';
import Box from 'elements/Box';
import {
  Avatar, Button, Chip, IconButton, InputAdornment, Tab, Tabs, TextField, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import AlertDialog from 'elements/AlertDialog';
import { useSnackbar } from 'notistack';
import UsersEditor from './UsersEditor';
import EmptyElement from 'elements/EmptyElement';
import { ApiRequest } from 'GlobalFunctions';
import { VscVerifiedFilled } from "react-icons/vsc";

export default function UsersList(props: any) {
  const { setLoading, company_id = null, is_popup = false } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [openEditor, setOpenEditor] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [refreshList, setRefreshList] = useState(0);

  useEffect(() => {
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadList = async () => {
    setLoading(true);
    await ApiRequest({
      url: "/users/list",
      method: "GET",
      query: { full_list: 1 },
      setResponse: (response: any) => {
        setAllUsers(response.list || []);
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshList]);

  // Reset search when tab changes
  useEffect(() => { setSearch(''); }, [activeTab]);

  const sortByLoginDesc = (a: any, b: any) => {
    if (!a.last_login && !b.last_login) return 0;
    if (!a.last_login) return 1;
    if (!b.last_login) return -1;
    return b.last_login.localeCompare(a.last_login);
  };

  const sortByCreatedDesc = (a: any, b: any) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return b.created_at.localeCompare(a.created_at);
  };

  const patients     = useMemo(() => [...allUsers.filter(u => u.role_id === 1)].sort(sortByLoginDesc), [allUsers]);
  const kinesiologists = useMemo(() => [...allUsers.filter(u => u.role_id === 2 && u.is_register_approved !== 0)].sort(sortByLoginDesc), [allUsers]);
  const pending      = useMemo(() => [...allUsers.filter(u => u.role_id === 2 && u.is_register_approved === 0)].sort(sortByCreatedDesc), [allUsers]);

  const tabs = [
    { label: 'Pacientes', list: patients },
    { label: 'Kinesiólogos', list: kinesiologists },
    { label: 'Por Aprobar', list: pending, isPending: true },
  ];

  const currentList = useMemo(() => {
    const base = tabs[activeTab]?.list || [];
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(u =>
      `${u.name} ${u.lastname}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, activeTab, search]);

  const handleDelete = (user_id: number) => { setActiveItem(user_id); setOpenAlert(true); };

  const deleteItem = () => {
    ApiRequest({
      url: '/users/deleteItem',
      method: 'POST',
      body: { user_id: activeItem },
      setResponse: (response: any) => {
        setOpenAlert(false);
        if (response.status === 'success') {
          enqueueSnackbar('Usuario eliminado con éxito', { variant: 'success' });
          setRefreshList((r) => r + 1);
        } else {
          enqueueSnackbar(response.message || 'Error al eliminar usuario', { variant: 'error' });
        }
      },
      onError: (msg: string) => {
        setOpenAlert(false);
        enqueueSnackbar(msg, { variant: 'error' });
      },
    });
  };

  const statusChip = (approved: any) => {
    if (approved === 0) return <Chip label="Por aprobar" color="warning" size="small" sx={{ fontWeight: 600, fontSize: 11 }} />;
    if (approved === 1) return <Chip label="Aprobado" color="success" size="small" sx={{ fontWeight: 600, fontSize: 11 }} />;
    if (approved === 2) return <Chip label="Rechazado" color="error" size="small" sx={{ fontWeight: 600, fontSize: 11 }} />;
    return null;
  };

  const isNurseTab = activeTab !== 0;

  const thSx = { color: '#94a3b8', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' as const, borderBottom: '1px solid #f1f5f9', py: 1.5 };

  return (
    <div id='UsersList' className='content'>
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Usuarios</h2>
            <span className="h2_subtitle">
              {patients.length + kinesiologists.length + pending.length} usuarios registrados
            </span>
          </div>
        </div>

        <Box id='AdminList' className={!is_popup ? 'box100' : 'boxModal'} style={{ padding: 0, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #e8edf4',
              px: 2,
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: 13, minHeight: 48, color: '#64748b' },
              '& .Mui-selected': { color: '#005F6A !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#005F6A' },
            }}
          >
            <Tab label={`Pacientes (${patients.length})`} />
            <Tab label={`Kinesiólogos (${kinesiologists.length})`} />
            <Tab
              label={`Por Aprobar (${pending.length})`}
              sx={{ color: pending.length > 0 ? '#d97706 !important' : undefined }}
            />
          </Tabs>

          {/* Search */}
          <div style={{ padding: '12px 16px 4px' }}>
            <TextField
              size="small"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 },
              }}
            />
          </div>


          <div style={{ padding: '8px 0 16px' }}>
            {currentList.length === 0 ? (
              <div style={{ padding: '40px 0' }}>
                <EmptyElement title="Sin usuarios" subtitle="No hay usuarios en esta categoría" />
              </div>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thSx, pl: 3 }}>Usuario</TableCell>
                      {isNurseTab && (
                        <>
                          <TableCell sx={thSx}>Estado</TableCell>
                          <TableCell sx={thSx}>Comisión</TableCell>
                        </>
                      )}
                      {activeTab === 2 && <TableCell sx={thSx}>Registro</TableCell>}
                      <TableCell sx={thSx}>Último acceso</TableCell>
                      <TableCell sx={{ ...thSx, pr: 2 }} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentList.map((user) => (
                      <TableRow
                        key={user.user_id}
                        hover
                        sx={{ '&:last-child td': { border: 0 }, '&:hover': { backgroundColor: '#f8fafc' } }}
                      >
                        <TableCell sx={{ pl: 3, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Avatar alt={user.name} src={user.image} sx={{ width: 38, height: 38, bgcolor: '#005F6A', fontSize: 16 }}>
                              {user.name?.[0]}
                            </Avatar>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {user.name} {user.lastname}
                                {user.commission_consent === 1 && <VscVerifiedFilled style={{ fontSize: 15, color: '#005F6A' }} />}
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{user.email}</div>
                            </div>
                          </div>
                        </TableCell>

                        {isNurseTab && (
                          <>
                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 1.5 }}>
                              {statusChip(user.is_register_approved)}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 1.5 }}>
                              {user.commission_consent === 1 ? (
                                <Chip
                                  label="Aceptada"
                                  size="small"
                                  sx={{ background: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: 11 }}
                                />
                              ) : (
                                <span style={{ fontSize: 12, color: '#cbd5e1' }}>Pendiente</span>
                              )}
                            </TableCell>
                          </>
                        )}

                        {activeTab === 2 && (
                          <TableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 1.5 }}>
                            {user.created_at ? (
                              <span style={{ fontSize: 13, color: '#475569' }}>{new Date(user.created_at).toLocaleDateString('es-CL')}</span>
                            ) : (
                              <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 1.5 }}>
                          {user.last_login ? (
                            <span style={{ fontSize: 13, color: '#475569' }}>{user.last_login}</span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>Sin acceso</span>
                          )}
                        </TableCell>

                        <TableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 1.5, pr: 2 }}>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ mr: 1, textTransform: 'none', fontSize: 13, minWidth: 0, px: 1.5, background: '#005F6A', '&:hover': { background: '#0f3d73' } }}
                            startIcon={<ModeEditIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => { setActiveItem(user.user_id); setOpenEditor(true); }}
                          >
                            Editar
                          </Button>
                          {isNurseTab && (
                            <Tooltip title="Ver perfil público" placement="top">
                              <IconButton
                                size="small"
                                sx={{ mr: 0.5, color: '#64748b', '&:hover': { color: '#005F6A' } }}
                                onClick={() => window.open(`/kinesiologos/${user.user_id}`, '_blank')}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton size="small" color="error" onClick={() => handleDelete(user.user_id)}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </Box>
      </div>

      <AlertDialog open={openAlert} setOpen={setOpenAlert} subtitle="¿Está seguro que desea eliminar este usuario?" send={deleteItem} />

      <UsersEditor
        setLoading={setLoading}
        openEditor={openEditor}
        setOpenEditor={setOpenEditor}
        user_id={activeItem}
        loadList={loadList}
        company_id={company_id}
      />
    </div>
  );
}
